import { GridSortDirection } from '@mui/x-data-grid/models/gridSortModel';

export interface TypedGridSortItem<T> {
  /**
   * The column field identifier.
   */
  field: keyof T & string;
  /**
   * The direction of the column that the grid should sort.
   */
  sort: GridSortDirection;
}

export type TypedGridSortModel<T> = readonly TypedGridSortItem<T>[];

export type SearchableFilterInput<T> = {
  [K in keyof T]:
    | SearchableIDFilterInput
    | SearchableStringFilterInput
    | SearchableFloatFilterInput
    | SearchableIntFilterInput
    | null;
} & {
  and?: Array<SearchableFilterInput<T> | null> | null;
  or?: Array<SearchableFilterInput<T> | null> | null;
  not?: SearchableFilterInput<T> | null;
};

export type SearchableSortInput = {
  field?: string | null;
  direction?: SearchableSortDirection | null;
};

export enum SearchableSortDirection {
  asc = 'asc',
  desc = 'desc',
}

export interface GraphQLQueryResponse<T> {
  [key: string]: GraphQLQueryResult<T>;
}

export interface GraphQLQueryResult<T> {
  items: T[];
  nextToken?: string;
  total: number;
  queryString?: string;
}

export interface GraphQLFetchResponse<T> {
  errors?: { message: string }[];
  data?: T;
}

export enum RelationalOperator {
  EQUAL = '=',
  NOT_EQUAL = '<>',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN_OR_EQUAL = '<=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  NOT_IN = 'NOT IN',
  CONTAINS_ANY = 'CONTAINS_ANY',
  CONTAINS_NONE = 'CONTAINS_NONE',
  CONTAINS_ALL = 'CONTAINS_ALL',
}

export type FilterRowSelectionValue = string | number | boolean | Date | null;

export interface FilterRowSelection {
  relationalOperator: RelationalOperator;
  value: FilterRowSelectionValue[];
  externalUpdateHash?: string; // Used to determine if the filter value was updated by an external source
}

export type FilterSelections = Record<string, FilterRowSelection>;

export interface ExportDataProcessorEvent {
  id: string;
  listType: string;
  exportName: string;
  filterInput: string;
  filterFields?: string | null;
  uniqueCommunities?: string[];
  uniqueLocs?: string[];
  status: string;
  nextToken?: string;
  totalProcessedRecords?: number;
  outputFormat?: string | null;
  uniqueBy?: string[];
  reportId?: string | null;
}

export interface DbColumnNameValueRecord {
  [key: string]: FilterRowSelection['value'][0];
}

export type SearchableIDFilterInput = {
  ne?: string | null;
  gt?: string | null;
  lt?: string | null;
  gte?: string | null;
  lte?: string | null;
  eq?: string | null;
  match?: string | null;
  matchPhrase?: string | null;
  matchPhrasePrefix?: string | null;
  multiMatch?: string | null;
  exists?: boolean | null;
  wildcard?: string | null;
  regexp?: string | null;
  range?: Array<string | null> | null;
};

export type SearchableStringFilterInput = {
  ne?: string | null;
  gt?: string | null;
  lt?: string | null;
  gte?: string | null;
  lte?: string | null;
  eq?: string | null;
  match?: string | null;
  matchPhrase?: string | null;
  matchPhrasePrefix?: string | null;
  multiMatch?: string | null;
  exists?: boolean | null;
  wildcard?: string | null;
  regexp?: string | null;
  range?: Array<string | null> | null;
};

export type SearchableIntFilterInput = {
  ne?: number | null;
  gt?: number | null;
  lt?: number | null;
  gte?: number | null;
  lte?: number | null;
  eq?: number | null;
  range?: Array<number | null> | null;
};

export type SearchableFloatFilterInput = {
  ne?: number | null;
  gt?: number | null;
  lt?: number | null;
  gte?: number | null;
  lte?: number | null;
  eq?: number | null;
  range?: Array<number | null> | null;
};

export type SearchableBooleanFilterInput = {
  eq?: boolean | null;
  ne?: boolean | null;
};

export type GraphQLQuery<T> = {
  [K in keyof T]?:
    | SearchableIDFilterInput
    | SearchableStringFilterInput
    | SearchableIntFilterInput
    | SearchableFloatFilterInput
    | null;
} & {
  and?: Array<GraphQLQuery<T> | null> | null;
  or?: Array<GraphQLQuery<T> | null> | null;
  not?: GraphQLQuery<T> | null;
  eq?: string;
  wildcard?: string;
};
